// El canal de frames en directo de un encargo.
//
// **No usa `EventSource`**, aunque esto sea SSE: `EventSource` no deja poner
// cabeceras, y esta API pide el token en `Authorization`. Se lee el flujo con
// `fetch`, que si las admite.
//
// Si el canal no se puede abrir o se corta, quien llama se entera y pregunta el
// estado: el encargo vive en la base, no en la conexion.

import { RAG_API_URL, getAuthToken } from '@/config/env'
import type { Run } from './brainApi'

/** Los frames que trae un trozo de flujo, en orden.
 *
 * Es puro a proposito: partir SSE es lo unico de esto que puede fallar por un
 * caracter, y probarlo no deberia necesitar ni red ni navegador.
 */
export function framesOf(chunk: string): { event: string; data: string }[] {
  return chunk
    .split('\n\n')
    .map((frame) => {
      const event = /^event: (.+)$/m.exec(frame)?.[1]
      const data = /^data: (.+)$/m.exec(frame)?.[1]
      return event && data ? { event, data } : null
    })
    .filter((frame): frame is { event: string; data: string } => frame !== null)
}

export function watchRun(
  runId: string,
  onState: (partial: Pick<Run, 'status' | 'plan' | 'steps' | 'events'>) => void,
): () => void {
  const stop = new AbortController()

  void (async () => {
    const token = getAuthToken()
    const response = await fetch(
      `${RAG_API_URL}/api/v1/rag/agent/v1/runs/${runId}/events/`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: stop.signal,
      },
    )
    if (!response.ok || !response.body) return

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    // Lo que quedo a medias del trozo anterior. Un aviso puede partirse por la
    // mitad entre dos lecturas, y tratar cada trozo por separado perderia justo
    // el que estaba cruzando.
    let leftover = ''

    for (;;) {
      const { done, value } = await reader.read()
      if (done) return
      leftover += decoder.decode(value, { stream: true })
      const lastBreak = leftover.lastIndexOf('\n\n')
      if (lastBreak === -1) continue
      const whole = leftover.slice(0, lastBreak + 2)
      leftover = leftover.slice(lastBreak + 2)
      for (const frame of framesOf(whole)) {
        if (frame.event === 'state') onState(JSON.parse(frame.data))
      }
    }
  })().catch(() => {
    // Perder el canal no es un error que contar: quien llama sigue preguntando.
  })

  return () => stop.abort()
}
